import { supabase } from './supabase';
import { User, Session } from '@supabase/supabase-js';

export interface AuthUser {
  id: string;
  email: string;
  user_metadata?: {
    firstname?: string;
    lastname?: string;
    role?: string;
  };
}

export interface VenueUserProfile {
  id: string;
  auth_user_id: string;
  firstname: string;
  lastname: string;
  email: string;
  role: string;
  status: string;
  organization_id?: string;
  organization_name?: string;
  plan_tier?: 'starter' | 'essential' | 'pro' | 'enterprise' | 'custom';
  access_profile?: {
    id: string;
    code: string;
    name: string;
    is_system?: boolean;
    is_locked?: boolean;
  } | null;
  permissions?: string[];
  subscription?: {
    status?: string;
    billing_cycle?: string | null;
    current_period_end?: string | null;
    cancel_at_period_end?: boolean;
    pending_plan_tier?: string | null;
    pending_effective_at?: string | null;
    past_due_since?: string | null;
  } | null;
  venues_ids?: string[];
  [key: string]: any;
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;

  const profile = await getVenueUserProfile(data.user.id);
  if (!profile) {
    // Avoid orphan Supabase sessions when profile RPC/REST is temporarily unavailable.
    await supabase.auth.signOut();
    throw new Error('Could not load user profile. Please try again.');
  }

  return {
    session: data.session,
    user: data.user,
    profile,
  };
}

/**
 * Sign out
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Get current session
 */
export async function getSession(): Promise<Session | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

/**
 * Refresh the current session (get new access token)
 */
export async function refreshSession(): Promise<Session | null> {
  const { data: { session } } = await supabase.auth.refreshSession();
  return session;
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<User | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

/**
 * Listen to auth state changes
 */
export function onAuthStateChange(
  callback: (session: Session | null, user: User | null) => void
) {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(session, session?.user ?? null);
  });
}

/**
 * Reset password
 */
export async function resetPassword(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/new-password`,
  });
  if (error) throw error;
}

/**
 * Update password
 */
export async function updatePassword(newPassword: string) {
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  if (error) throw error;
}

/**
 * Get venue user profile by auth user id
 */
function mapVenueUserProfile(user: {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  photo_url: string;
  role: string;
  status: string;
  isverified: boolean;
  schedule_sync: boolean;
  organization_id: string;
  organization_name?: string;
  plan_tier?: string;
  access_profile?: VenueUserProfile['access_profile'];
  permissions?: string[];
  subscription?: VenueUserProfile['subscription'];
  auth_user_id: string;
  venues_ids: string[];
  created_at: string;
  updated_at: string;
}): VenueUserProfile {
  return {
    id: user.id,
    auth_user_id: user.auth_user_id,
    displayName: user.displayName,
    firstname: user.firstName,
    lastname: user.lastName,
    email: user.email,
    role: user.role,
    status: user.status,
    organization_id: user.organization_id,
    organization_name: user.organization_name,
    plan_tier: (user.plan_tier || 'starter') as VenueUserProfile['plan_tier'],
    access_profile: user.access_profile ?? null,
    permissions: user.permissions ?? [],
    subscription: user.subscription ?? null,
    venues_ids: user.venues_ids,
    photo_url: user.photo_url,
    isverified: user.isverified,
    schedule_sync: user.schedule_sync,
    created_at: user.created_at,
    updated_at: user.updated_at,
  } as VenueUserProfile;
}

function isPostgrestUnavailable(error: unknown) {
  const message = String((error as any)?.message || error || '');
  return (
    (error as any)?.status === 503 ||
    /503|upstream connect error|connection failure|delayed connect error/i.test(message)
  );
}

async function fetchVenueUserProfileViaEdge(): Promise<VenueUserProfile | null> {
  const { invokeFunction } = await import('./edgeFunctionService');
  const result = await invokeFunction<{ user: Parameters<typeof mapVenueUserProfile>[0] }>(
    'venue-user-profile',
    {}
  );
  if (!result?.user) return null;
  const mapped = mapVenueUserProfile(result.user);
  if (!mapped.organization_id) return null;
  return mapped;
}

export async function getVenueUserProfile(_authUserId: string): Promise<VenueUserProfile | null> {
  const { callRPC } = await import('./databaseService');

  try {
    const result = await callRPC<{
      user: Parameters<typeof mapVenueUserProfile>[0];
    }>('get_venue_user_profile');

    if (!result?.user) return null;
    const mapped = mapVenueUserProfile(result.user);
    if (!mapped.organization_id) return null;
    return mapped;
  } catch (error) {
    if (!isPostgrestUnavailable(error)) {
      console.error('Error fetching venue user profile:', error);
      return null;
    }

    console.warn('PostgREST unavailable; falling back to venue-user-profile edge function');
    try {
      return await fetchVenueUserProfileViaEdge();
    } catch (edgeError) {
      console.error('Edge profile fallback failed:', edgeError);
      return null;
    }
  }
}

